import React from 'react'
import { Box, AppBar, Toolbar, Typography, Stack } from '@mui/material'
import logoSplash from '../../assets/logoSplash.png'

/**
 * Coquille commune à tous les écrans.
 *
 * Structure en 3 zones :
 *   [ header fixe ] · [ contenu défilant ] · [ barre d'actions collée en bas ]
 *
 * La hauteur totale utilise --app-vh (100dvh) : contrairement à 100vh, elle
 * ne saute pas quand la barre d'URL de Safari/Chrome mobile apparaît ou
 * disparaît. Seule la zone centrale défile, la barre d'actions reste donc
 * toujours atteignable au pouce.
 */
export const AppShell = ({
  title,
  subtitle,
  action,
  leading,
  children,
  footer,
  maxWidth = 560,
  disableContentPadding = false,
  contentSx,
}) => {
  return (
    <Box
      sx={{
        height: 'var(--app-vh)',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        overflow: 'hidden',
      }}
    >
      <AppBar
        position="relative"
        sx={{
          flex: '0 0 auto',
          // Le header peint sous l'encoche, son contenu reste en dessous
          pt: 'var(--safe-top)',
          pl: 'var(--safe-left)',
          pr: 'var(--safe-right)',
          zIndex: (t) => t.zIndex.appBar,
        }}
      >
        <Toolbar sx={{ gap: 1, px: { xs: 1.5, sm: 2 } }}>
          {leading}
          <Stack sx={{ flexGrow: 1, minWidth: 0 }} justifyContent="center">
            {title ? (
              <>
                <Typography
                  variant="h6"
                  noWrap
                  sx={{ lineHeight: 1.2, fontWeight: 700 }}
                >
                  {title}
                </Typography>
                {subtitle ? (
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{ color: 'text.secondary', fontWeight: 600 }}
                  >
                    {subtitle}
                  </Typography>
                ) : null}
              </>
            ) : (
              <Box
                component="img"
                src={logoSplash}
                alt="TicketNGo"
                sx={{
                  height: { xs: 26, sm: 30 },
                  width: 'auto',
                  maxWidth: '100%',
                  objectFit: 'contain',
                  // Sans alignSelf, l'étirement du conteneur flex forcerait
                  // l'image à occuper toute la largeur de la barre.
                  alignSelf: 'flex-start',
                  display: 'block',
                }}
              />
            )}
          </Stack>
          {action}
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          pl: 'var(--safe-left)',
          pr: 'var(--safe-right)',
          // Sans barre d'actions, on garde la marge basse de sécurité ici
          pb: footer ? 0 : 'var(--safe-bottom)',
          ...contentSx,
        }}
      >
        <Box
          sx={{
            maxWidth,
            mx: 'auto',
            width: '100%',
            px: disableContentPadding ? 0 : { xs: 2, sm: 3 },
            py: disableContentPadding ? 0 : { xs: 2, sm: 3 },
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {children}
        </Box>
      </Box>

      {footer ? (
        <Box
          sx={{
            flex: '0 0 auto',
            bgcolor: 'background.default',
            borderTop: '1px solid',
            borderColor: 'divider',
            pb: 'var(--safe-bottom)',
            pl: 'var(--safe-left)',
            pr: 'var(--safe-right)',
            boxShadow: '0 -12px 28px -20px rgba(0,0,0,0.9)',
          }}
        >
          <Box
            sx={{
              maxWidth,
              mx: 'auto',
              width: '100%',
              px: { xs: 2, sm: 3 },
              py: { xs: 1.5, sm: 2 },
            }}
          >
            {footer}
          </Box>
        </Box>
      ) : null}
    </Box>
  )
}

export default AppShell
